/**
 * Vaqt maydoni mantiqi (components/ui/field.tsx: TimeInput). Brauzerning `type="time"` maydoni ishlatilmaydi: u brauzer
 * tilida chiziladi (inglizcha brauzerda 12 soatlik "11:07 PM"), tor joyda kesiladi va dizaynga bo'ysunmaydi.
 *
 * Qiymat doim 24 soatlik "HH:MM" yoki "HH:MM:SS" matn. Maydon segmentlar (soat, daqiqa, soniya) bo'yicha yoziladi:
 * raqam segmentni to'ldiradi va keyingisiga o'tadi (native maydon odati), ↑/↓ qadam, ←/→ segment, Backspace -- 00.
 */
export type TimeParts = [number, number, number];

const MAX: TimeParts = [23, 59, 59];

export const segmentCount = (seconds: boolean) => (seconds ? 3 : 2);

/** "9:5" -> [9,5,0]; "23:07:56" -> [23,7,56]; faqat raqam "2307" / "230756" ham; noto'g'ri -> null */
export function parseTime(text: string, seconds: boolean): TimeParts | null {
  const s = String(text ?? "").trim();
  if (!s) return null;
  let nums: string[];
  if (/^\d+$/.test(s)) {
    if (s.length !== 4 && s.length !== 6) return null;
    nums = s.match(/\d\d/g) ?? [];
  } else {
    nums = s.split(":");
  }
  if (nums.length < 2 || nums.length > 3 || nums.some((n) => !/^\d{1,2}$/.test(n))) return null;
  const parts: TimeParts = [Number(nums[0]), Number(nums[1]), seconds ? Number(nums[2] ?? 0) : 0];
  return parts.every((v, i) => v <= MAX[i]) ? parts : null;
}

const two = (n: number) => String(n).padStart(2, "0");

/** [23,7,56] -> "23:07:56" (seconds) yoki "23:07" */
export const formatTime = (parts: TimeParts, seconds: boolean) => parts.slice(0, segmentCount(seconds)).map(two).join(":");

/** Bo'sh maydon ko'rinishi */
export const emptyTime = (seconds: boolean) => (seconds ? "--:--:--" : "--:--");

/** Segmentning matndagi o'rni: [boshi, oxiri) */
export const segmentRange = (seg: number): [number, number] => [seg * 3, seg * 3 + 2];

/** Kursor turgan joydagi segment (oxirgi segmentdan oshmaydi) */
export const segmentAt = (caret: number, seconds: boolean) => Math.max(0, Math.min(segmentCount(seconds) - 1, Math.floor(caret / 3)));

export interface Typed {
  parts: TimeParts;
  /** Shu segmentga yozilgan, hali to'lmagan birinchi raqam */
  pending: string;
  /** Segment to'ldi -- keyingisiga o'tiladi */
  advance: boolean;
}

/**
 * Segmentga raqam yozish. Birinchi raqam o'nlik chegaradan katta bo'lsa (soat 3-9, daqiqa/soniya 6-9) segment darhol
 * to'ladi; ikkinchi raqam bilan chegaradan oshsa ("2" dan keyin "9" -- 29 soat yo'q) oxirgi raqam yangi boshlanish.
 */
export function typeDigit(parts: TimeParts, seg: number, pending: string, digit: string): Typed {
  const d = Number(digit);
  const max = MAX[seg];
  const next = [...parts] as TimeParts;
  if (pending) {
    const candidate = Number(pending) * 10 + d;
    if (candidate <= max) {
      next[seg] = candidate;
      return { parts: next, pending: "", advance: true };
    }
  }
  next[seg] = d;
  const full = d > Math.floor(max / 10);
  return { parts: next, pending: full ? "" : digit, advance: full };
}

/** Strelka bilan qadam: chegaradan oshsa aylanadi (23 -> 00, 00 -> 59) */
export function stepSegment(parts: TimeParts, seg: number, delta: number): TimeParts {
  const next = [...parts] as TimeParts;
  const span = MAX[seg] + 1;
  next[seg] = (((next[seg] + delta) % span) + span) % span;
  return next;
}

/** Segmentni tozalash (Backspace): 00 */
export function clearSegment(parts: TimeParts, seg: number): TimeParts {
  const next = [...parts] as TimeParts;
  next[seg] = 0;
  return next;
}
